/**
 * Structural subset of Angular's NgZone. Declared structurally so this base
 * util stays framework-loose (and trivially fakeable in tests) while letting
 * Angular callers hand in their real zone.
 */
export interface ZoneLike {
  runOutsideAngular<T>(fn: () => T): T;
  run<T>(fn: () => T): T;
}

export interface WaitForElementOptions {
  /** How long to wait for the element before giving up. Defaults to 5000ms. */
  timeoutMs?: number;
  /** Where to query and observe from. Defaults to document. */
  root?: ParentNode;
  /**
   * When provided, the mutation observer and timeout run OUTSIDE the Angular
   * zone and only the final resolution re-enters it. Without this, a document-
   * wide render burst (a lazy-loaded page building a grid emits thousands of
   * mutations) schedules an Angular change-detection pass per mutation, and
   * each CD pass mutates the DOM again — a self-sustaining CD loop that hard-
   * freezes the main thread. Observed on walkthrough page-nav steps.
   */
  ngZone?: ZoneLike;
}

/**
 * True when `el` is actually rendered (occupies a layout box), false when it is
 * present in the DOM but not visible, e.g. a control inside a closed
 * `[hidden]`/`display:none` dialog. `offsetParent` is null when the element or
 * any ancestor is `display:none`; `getClientRects()` is the fallback for
 * `position: fixed` elements (whose `offsetParent` is also null but which DO
 * render).
 */
function isElementVisible(el: HTMLElement): boolean {
  return el.offsetParent !== null || el.getClientRects().length > 0;
}

/** rAF when available (browser); setTimeout fallback keeps jsdom/SSR working. */
function nextFrame(cb: () => void): void {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => cb());
  } else {
    setTimeout(cb, 16);
  }
}

/**
 * Resolves with the first VISIBLE element matching `selector`, waiting for it to
 * both appear in the DOM and become visible if it is not yet. Resolves with
 * `null` if no visible match appears within `timeoutMs`.
 *
 * This is the resilience primitive behind the walkthrough player. It handles two
 * cases, both common with Kendo `[hidden]`-pattern dialogs:
 *  - the target is ABSENT at step start and added later (a `*-page` renders it), and
 *  - the target is PRESENT but HIDDEN at step start. A declarative `<kendo-dialog>`
 *    keeps its controls in the DOM and toggles visibility via `[hidden]`, so a
 *    control exists immediately but has no layout box until the dialog opens.
 *
 * Resolving on mere presence would spotlight a zero-rect, invisible control, so we
 * gate on visibility. Opening a `[hidden]` dialog is an ATTRIBUTE change (the
 * `hidden` attr drops), not a childList mutation, so the observer watches attribute
 * changes too, not just childList.
 *
 * Cost discipline: the match check reads layout (`offsetParent`/
 * `getClientRects`), so running it per mutation during a page render is layout
 * thrash. The observer therefore only SCHEDULES a check, coalesced to at most
 * one per animation frame, no matter how many mutations land in that frame.
 */
export function waitForElement(
  selector: string,
  options: WaitForElementOptions = {}
): Promise<HTMLElement | null> {
  const { timeoutMs = 5000, root = document, ngZone } = options;

  const existing = root.querySelector<HTMLElement>(selector);
  if (existing && isElementVisible(existing)) {
    return Promise.resolve(existing);
  }

  const outside = <T>(fn: () => T): T =>
    ngZone ? ngZone.runOutsideAngular(fn) : fn();
  const inside = (fn: () => void): void => {
    if (ngZone) {
      ngZone.run(fn);
    } else {
      fn();
    }
  };

  return new Promise<HTMLElement | null>((resolve) => {
    let settled = false;
    let checkScheduled = false;
    let observer: MutationObserver;
    let timer: ReturnType<typeof setTimeout>;

    function settle(el: HTMLElement | null): void {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      observer.disconnect();
      // Resolution is the one edge that must re-enter the zone: the caller
      // reacts by highlighting/advancing, which needs change detection.
      inside(() => resolve(el));
    }

    function checkNow(): void {
      checkScheduled = false;
      if (settled) {
        return;
      }
      const found = root.querySelector<HTMLElement>(selector);
      if (found && isElementVisible(found)) {
        settle(found);
      }
    }

    function scheduleCheck(): void {
      if (checkScheduled || settled) {
        return;
      }
      checkScheduled = true;
      nextFrame(checkNow);
    }

    outside(() => {
      observer = new MutationObserver(scheduleCheck);
      const observeTarget: Node =
        root instanceof Document
          ? root.body ?? root.documentElement
          : (root as Node);
      observer.observe(observeTarget, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden', 'style', 'class'],
      });
      timer = setTimeout(() => settle(null), timeoutMs);
    });
  });
}
